// ⚠️ BANCO DE PRUEBAS — VARIANTE V2 DEL MODO MODELO. TEMPORAL.
//
// Copia congelada de `PosePicker.tsx → PosePickerInline` para poder rediseñar la grilla de Poses del paso 2 sin
// tocar el modo que está en producción (`/interfaz-v2/modo/modelo`).
// Ver ADR-2026-08-21-modelo-tres-variantes-de-diseno.
//
// Un fix de comportamiento en el original NO llega acá: hay que portarlo a mano.
// Cuando el rediseño cierre, lo que gane se promueve al original y este archivo se borra.

import React, { useEffect, useState } from 'react';
import {
  Check,
  ChevronLeft,
  ChevronRight,
  Plus,
  Sparkles,
  Upload,
} from 'lucide-react';
import { POSE_CATEGORIES, type PoseV2 } from '../posesApi';
import { HoverScrollCarousel } from '../HoverScrollCarousel';
import { AccionCard } from '../components/AccionCard';
import { SwitchSeleccionMultiple } from '../components/SwitchSeleccionMultiple';
import { usePalabraDeModelo } from '../../../lib/nombreDeModelo';

// ─── Inline (desktop) ─────────────────────────────────────────────────

/** Cuántas cards entran en el carrusel antes de la de "Ver más". */
const INLINE_VISIBLE = 12;

interface PosePickerInlineProps {
  poses: PoseV2[];
  /** Poses propias del cliente. No están en `poses` (catálogo global) y sólo se
   *  muestran acá si están elegidas — el carrusel no es su vitrina, pero una
   *  pose elegida tiene que verse en la pantalla principal. */
  customPoses?: PoseV2[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  onOpenSheet: () => void;
  /**
   * Abre el sheet de poses DIRECTO en "Subí tu propia pose".
   *
   * Sólo la usa la variante `grilla`, que dibuja una tarjeta de subir al final del
   * catálogo (ADR-2026-08-20-subir-pose-al-final-de-la-grilla). Sin ella esa tarjeta no
   * se dibuja: el carrusel ya sale al sheet por "Ver más", y ahí adentro la de subir es
   * la primera de la grilla.
   */
  onSubirPose?: () => void;
  /** Si true, oculta el label "Pose" interno (lo provee el caller). */
  hideLabel?: boolean;
  /** Si true, fuerza selección exclusiva (radio-button). Lo usa el
   *  modo varios-modelos: con N personas en la pose no tiene sentido
   *  generar variaciones por pose. */
  singleSelect?: boolean;
  /** Deja quedarse en CERO poses (ver `toggle`). */
  permitirVacio?: boolean;
  /**
   * Columnas FIJAS de la grilla. Sin esto, las que entren por ancho.
   *
   * Misma prop que ya tenía el picker de modelos: en el panel lateral las dos grillas
   * tienen que verse igual, y `auto-fill` las desacoplaba —cada una metía las columnas
   * que le permitía la proporción de SU tarjeta, así que en el mismo ancho daban
   * números distintos—.
   */
  columnasGrilla?: number;
  /** Toggle "Seleccionar múltiples" (vive en el PosePickerSheet). Con
   *  false el tap reemplaza la selección en vez de agregar. Default
   *  true para no cambiar a los consumidores que no lo pasan. */
  multiPoseEnabled?: boolean;
  /**
   * Commit del toggle "Utilizar varias poses". Si se pasa, el picker dibuja el
   * interruptor ARRIBA de la grilla; si no, no lo dibuja y `multiPoseEnabled` manda solo.
   *
   * Es el mismo contrato que el del sheet (`PosePickerSheet.onMultiPoseEnabledChange`):
   * el control existe donde el caller lo maneja — ver
   * ADR-2026-09-04-el-interruptor-de-varias-poses-tambien-en-desktop.
   */
  onMultiPoseEnabledChange?: (enabled: boolean) => void;
  /**
   * Reemplaza el carrusel horizontal por una GRILLA que scrollea hacia abajo, con el
   * catálogo ENTERO (el carrusel corta en 12).
   *
   * El alto de la tarjeta sale de `--alto-fila`, una variable CSS que pone el contenedor
   * de arriba para que entre una fila y MEDIA en el alto disponible; el ancho lo reparte
   * la grilla, que llena el contenedor sin dejar sobrante
   * (ADR-2026-08-20-grilla-fila-y-media).
   */
  grilla?: boolean;
}

export const PosePickerInline: React.FC<PosePickerInlineProps> = ({
  poses,
  customPoses = [],
  selectedIds,
  onChange,
  onOpenSheet,
  onSubirPose,
  hideLabel = false,
  singleSelect = false,
  permitirVacio = false,
  columnasGrilla,
  onUploadCustomPose,
  uploadingCustomPose = false,
  multiPoseEnabled = true,
  onMultiPoseEnabledChange,
  grilla = false,
}) => {
  // La palabra de pantalla para la persona de la foto — "Modelo" en Moda, "Avatar"
  // en el resto (ADR-2026-09-22-en-moda-el-modelo-se-llama-modelo).
  const palabra = usePalabraDeModelo();
  // El carrusel corta en 12: una pose elegida desde "Ver más" —o cualquiera de
  // las propias, que ni siquiera están en el catálogo— quedaba invisible acá, y
  // la pantalla principal no mostraba rastro de lo que estaba seleccionado. Las
  // adelantadas van PRIMERO y el corte se aplica después, así nunca las tapa.
  //
  // `adelantadas` es un pin, NO `selectedIds` en vivo: si el orden se
  // recalculara con cada tap, tocar una card la haría saltar al principio y el
  // carrusel se movería bajo el cursor. Mismo criterio que el `pinnedIdRef` del
  // ModelPicker. Se repinea sólo cuando hay una elegida que NO está visible,
  // que es justo el caso de volver del "Ver más".
  const [adelantadas, setAdelantadas] = useState<string[]>(selectedIds);
  /**
   * Categoría abierta en la grilla del panel.
   *
   * Son los MISMOS tabs del sheet de poses (`POSE_CATEGORIES` + "Mis poses"): con el
   * catálogo entero en una sola grilla, encontrar una pose concreta era scrollear a ojo.
   * Ver ADR-2026-08-24-poses-con-sus-categorias-en-el-panel.
   */
  const [categoria, setCategoria] = useState<string>('recomendado');
  /** Vista interna del picker: la grilla o el alta de una pose propia. */
  const [vista, setVista] = useState<'lista' | 'subir'>('lista');

  /**
   * Las categorías que tienen ALGO para mostrar — ADR-2026-08-25-los-filtros-de-pose-vacios-no-se-muestran.
   *
   * El catálogo que llega ya viene filtrado por la cantidad de personas de la
   * composición, y no todas las categorías existen en las tres cantidades: con
   * tres modelos, por ejemplo, no hay poses de "Detalle / Close-up". El tab
   * seguía ahí y abrirlo dejaba la grilla vacía (cuando existía la tarjeta
   * "Automática", con esa sola y nada más), que se lee como que se rompió algo.
   *
   * "Recomendados" no se filtra: no es una categoría sino el estado de arranque
   * —muestra el catálogo entero— y tiene que quedar siempre como salida.
   */
  const categoriasVisibles = React.useMemo(() => {
    const todas = [
      ...POSE_CATEGORIES,
      { id: 'mis_poses', label: 'Mis poses' },
    ];
    return todas.filter((c) => {
      if (c.id === 'recomendado') return true;
      if (c.id === 'mis_poses') return customPoses.length > 0;
      return poses.some((p) => (p.categoria || '') === c.id);
    });
  }, [poses, customPoses]);

  // Si la categoría abierta desaparece —cambió la cantidad de modelos y con ella
  // el catálogo— se vuelve a "Recomendados". Sin esto queda un filtro activo que
  // ya no tiene pill: la grilla se ve vacía y no hay dónde tocar para salir.
  useEffect(() => {
    if (!categoriasVisibles.some((c) => c.id === categoria)) {
      setCategoria('recomendado');
    }
  }, [categoriasVisibles, categoria]);
  const fileRef = React.useRef<HTMLInputElement>(null);
  // En grilla no hay corte: el contenedor scrollea, así que mostrar sólo 12 dejaría el
  // resto del catálogo afuera sin ninguna señal de que existe.
  const tope = grilla ? Number.POSITIVE_INFINITY : INLINE_VISIBLE;
  const visibles = React.useMemo(() => {
    const alFrente = adelantadas
      .map(
        (id) =>
          poses.find((p) => p.id === id) ??
          customPoses.find((p) => p.id === id),
      )
      .filter((p): p is PoseV2 => !!p);
    const yaEsta = new Set(alFrente.map((p) => p.id));
    // El filtro sólo vale en la GRILLA (el carrusel no dibuja los tabs). "Recomendados"
    // muestra el catálogo entero —es el estado de arranque, no una categoría— y
    // "Mis poses" sale de las propias del cliente, que no están en `poses`.
    const delCatalogo = !grilla
      ? poses
      : categoria === 'recomendado'
        ? poses
        : categoria === 'mis_poses'
          ? customPoses
          : poses.filter((p) => (p.categoria || '') === categoria);
    const resto = delCatalogo.filter((p) => !yaEsta.has(p.id));
    // Con una categoría abierta, lo elegido no se adelanta: rompería el filtro —una pose
    // de otra categoría aparecería primera— y es justo lo que el filtro viene a evitar.
    const conFrente =
      grilla && categoria !== 'recomendado' ? resto : [...alFrente, ...resto];
    return conFrente.slice(0, Math.max(tope, alFrente.length));
  }, [poses, customPoses, adelantadas, tope, grilla, categoria]);
  useEffect(() => {
    const hayInvisible = selectedIds.some(
      (id) => !visibles.some((v) => v.id === id),
    );
    if (hayInvisible) setAdelantadas(selectedIds);
  }, [selectedIds, visibles]);
  const toggle = (id: string) => {
    if (singleSelect || !multiPoseEnabled) {
      // Selección exclusiva: tap reemplaza. Si la selección ya es
      // exactamente esta pose, no hay nada que hacer (mínimo 1 — no se
      // deselecciona). Si venía >1 (ej. el user apagó el toggle desde
      // otro picker en un estado raro), el tap colapsa a [id].
      if (selectedIds.length === 1 && selectedIds[0] === id) return;
      onChange([id]);
      return;
    }
    if (selectedIds.includes(id)) {
      // La última pose se puede sacar sólo donde la pantalla lo banca (`permitirVacio`):
      // ahí el título lleva la cuenta y el CTA se apaga en cero, así que "ninguna" es un
      // estado visible y sin salida en falso. En el resto de los pickers sigue el mínimo
      // de 1 — son de paso, y salir de ahí sin pose dejaría al modo sin qué generar.
      if (selectedIds.length === 1 && !permitirVacio) return;
      onChange(selectedIds.filter((x) => x !== id));
      return;
    }
    onChange([...selectedIds, id]);
  };
  return (
    <div className="flex flex-col gap-3">
      {/* En grilla el catálogo entero ya está a la vista y scrollea, así que
          "Ver más" no lleva a ningún lado: la fila entera sobra (con `hideLabel`
          su único contenido era ese botón). */}
      {!grilla && (
        <div
          className={`flex items-center ${hideLabel ? 'justify-end' : 'justify-between'}`}
        >
          {!hideLabel && (
            <p className="text-[11px] uppercase tracking-wide font-medium text-[#3C3C43]/55">
              Pose
            </p>
          )}
          <button
            type="button"
            onClick={onOpenSheet}
            className="inline-flex items-center gap-0.5 text-brand-primary text-[12.5px] font-medium hover:opacity-75 transition-opacity"
          >
            Ver más
            <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>
      )}
      {/* Carrusel — mismo aspect que las cards de modelo. Tap toggle.
          La `key` dispara `animate-mode-swap` cuando el catálogo
          filtrado cambia (ej. al cambiar la cantidad de modelos en
          multi mode → poses de N personas distintas). */}
      {vista === 'subir' ? (
        // Alta de una pose propia SIN salir del panel — el mismo circuito que "Creá tu
        // modelo" en el picker de modelos (ADR-2026-08-24-subir-pose-sin-salir-del-panel).
        <div className="flex flex-col gap-4 min-h-full">
          <button
            type="button"
            onClick={() => setVista('lista')}
            className="inline-flex items-center gap-1 text-brand-primary text-[13px] hover:opacity-75 transition-opacity self-start"
          >
            <ChevronLeft className="w-3.5 h-3.5" />
            Volver
          </button>
          <div>
            <p className="text-[16px] font-semibold text-brand-black/90">
              Subí tu propia pose
            </p>
            <p className="text-[13.5px] text-[#3C3C43]/65 mt-1 leading-relaxed">
              Subí una foto con la postura que te gusta y Atlas la guarda como una
              pose tuya. Después la elegís igual que cualquier otra del catálogo,
              para generar con tu {palabra.singular} y tu producto.
            </p>
          </div>
          <div className="w-full flex items-center justify-center">
            <img
              src="/images/pose-explainer.png"
              alt="De una foto de referencia a la pose lista para usar"
              loading="lazy"
              className="w-auto max-w-full max-h-[280px] object-contain"
            />
          </div>
          <ul className="text-[13px] text-[#3C3C43]/65 list-disc pl-4 space-y-1.5 leading-snug">
            <li>Una foto nítida y de frente al cuerpo funciona mejor.</li>
            <li>
              El fondo y la ropa no importan: sólo se copia la postura, no la
              persona.
            </li>
          </ul>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={async (e) => {
              const file = e.target.files?.[0];
              // Se limpia SIEMPRE: sin esto, subir la misma foto dos veces seguidas no
              // dispara el `change` la segunda vez.
              e.target.value = '';
              if (!file || !onUploadCustomPose) return;
              await onUploadCustomPose(file);
              // De vuelta a la grilla, en "Mis poses": ahí está la que se acaba de crear.
              setVista('lista');
              setCategoria('mis_poses');
            }}
          />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={uploadingCustomPose}
            className="mt-auto w-full inline-flex items-center justify-center gap-2.5 py-3.5 px-6 rounded-2xl bg-brand-primary hover:bg-brand-primary-hover disabled:opacity-40 text-white text-[15.5px] font-semibold shadow-sm transition-colors"
          >
            {uploadingCustomPose ? (
              <>
                <span className="w-5 h-5 rounded-full border-2 border-white/40 border-t-white animate-spin" />
                Creando la pose…
              </>
            ) : (
              <>
                <Upload className="w-5 h-5" />
                Subir imagen con la pose
              </>
            )}
          </button>
        </div>
      ) : grilla ? (
        // Grilla de columnas IGUALES que llenan el ancho exacto del contenedor
        // (ADR-2026-08-20-grilla-fila-y-media): `auto-fill` mete todas las columnas de
        // al menos `9/16 del alto de fila` que entren, y el `1fr` reparte lo que sobra
        // ENTRE ellas. Con `flex-wrap` y ancho fijo por aspect, ese resto quedaba como
        // una franja blanca al final de cada fila.
        //
        // El alto manda (`--alto-fila`) y el ancho lo pone la columna, así que las
        // tarjetas terminan un pelo más anchas que 9:16 — el `object-cover` de la foto
        // absorbe la diferencia. Al revés (ancho fijo, alto por aspect) las filas no
        // cerrarían en la fracción exacta que deja asomar la de abajo.
        //
        // `content-start`: las filas se apilan desde arriba en vez de repartirse el alto
        // sobrante, que con pocas poses las dejaba flotando en el medio.
        <>
        {/* El interruptor "Utilizar varias poses", ARRIBA de todo —
            ADR-2026-09-04-el-interruptor-de-varias-poses-tambien-en-desktop.

            Es el MISMO control y el mismo copy que el del sheet de mobile
            (`PosePickerSheet`, ADR-2026-09-04-las-poses-toman-el-interruptor-de-los-modelos):
            la decisión es la misma en las dos superficies y tenerla en una sola dejaba al
            panel de desktop acumulando poses sin que nada lo dijera.

            Va antes que las pastillas de categoría porque cambia lo que hace cada tap que
            viene después, así que hay que leerlo ANTES de empezar a elegir.

            Sólo cuando el caller lo controla, y nunca con `singleSelect` (multi-modelo
            fuerza pose única) — mismo gate que el `multiToggleAvailable` del sheet. */}
        {onMultiPoseEnabledChange && !singleSelect && (
          <SwitchSeleccionMultiple
            titulo="Utilizar varias poses"
            activo={multiPoseEnabled}
            onChange={onMultiPoseEnabledChange}
            bajada={
              !multiPoseEnabled
                ? 'Se genera una imagen por cada pose que elijas.'
                : `${selectedIds.length} ${
                    selectedIds.length === 1 ? 'pose elegida' : 'poses elegidas'
                  } — una imagen por cada una.`
            }
          />
        )}
        {/* Tabs de categoría: los mismos del sheet, un punto más chicos (`h-8`, 12.5px)
            porque acá comparten ancho con el panel y no con la pantalla entera, y con
            `flex-wrap` para que bajen a una segunda fila en vez de recortarse. */}
        {/* Con una sola categoría en pie no hay nada que filtrar: la fila sería un
            botón que no cambia nada. */}
        {categoriasVisibles.length > 1 && (
        <div className="flex flex-wrap gap-1.5 mb-3">
          {categoriasVisibles.map(
            (c) => {
              const activa = categoria === c.id;
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setCategoria(c.id)}
                  className={`shrink-0 inline-flex items-center gap-1.5 px-3 h-8 rounded-full border text-[12.5px] font-medium transition-colors whitespace-nowrap ${
                    activa
                      ? 'bg-brand-primary text-white border-brand-primary'
                      : 'bg-white text-brand-black/90 border-neutral-200 hover:bg-neutral-50'
                  }`}
                >
                  {c.id === 'recomendado' && <Sparkles className="w-3.5 h-3.5" />}
                  {c.id === 'mis_poses' && <Plus className="w-3.5 h-3.5" />}
                  {c.label}
                </button>
              );
            },
          )}
        </div>
        )}
        <div
          className="grid content-start gap-3 animate-mode-swap"
          style={{
            gridTemplateColumns: columnasGrilla
              ? `repeat(${columnasGrilla}, minmax(0, 1fr))`
              : 'repeat(auto-fill, minmax(min(calc(var(--alto-fila, 240px) * 9 / 16), 100%), 1fr))',
          }}
        >
          {visibles.map((p, i) => {
            const isSel = selectedIds.includes(p.id);
            // PRUEBA: la miniatura sale de las imágenes de muestra, alternadas por
            // posición. La pose (id, nombre, prompt) no cambia — sólo la foto.
            // La foto REAL de la pose. La prueba de miniaturas de muestra (los maniquíes
            // de `posesPrueba.ts`) queda desactivada en esta variante: se pidió volver a
            // las originales. El flag sigue vivo para v1 y v3, que lo comparten.
            const thumb = p.imageUrl;
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => toggle(p.id)}
                className={`h-[var(--alto-fila,240px)] w-full rounded-2xl overflow-hidden bg-neutral-100 border-2 ${
                  isSel ? 'border-brand-primary' : 'border-neutral-200'
                } transition-all relative active:scale-[0.97]`}
              >
                {thumb && (
                  <img
                    src={thumb}
                    alt={p.nombre}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                )}
                {/* El nombre DENTRO de la foto, abajo, sobre un degradé negro corto —
                    ADR-2026-09-04-las-poses-muestran-su-nombre-sobre-la-foto. Es la misma
                    anatomía que las tarjetas de PRODUCTO de este modo
                    (`nombreSobreLaFoto` en `PickProductPanelModelo`): el degradé arranca
                    transparente y no llega ni a un sexto de la tarjeta, así que separa el
                    texto del fondo sin tapar la pose. */}
                <span className="absolute inset-x-0 bottom-0 px-2.5 pt-8 pb-2 bg-gradient-to-t from-black/85 via-black/45 to-transparent">
                  <span className="block text-left text-[12px] font-bold text-white truncate">
                    {p.nombre}
                  </span>
                </span>
                {isSel && (
                  <span className="absolute top-2 right-2 inline-flex items-center justify-center w-6 h-6 rounded-full bg-brand-primary text-white shadow">
                    <Check className="w-3.5 h-3.5" strokeWidth={3} />
                  </span>
                )}
              </button>
            );
          })}
          {/* Cierre del catálogo: "Subir pose". Va ÚLTIMA y no primera —al revés que
              en el sheet, donde compite con un catálogo paginado por categorías— porque
              acá la grilla es el catálogo entero y el orden cuenta una historia: mirá
              todas las poses que hay y, si ninguna sirve, subí la tuya
              (ADR-2026-08-20-subir-pose-al-final-de-la-grilla). Mide igual que una
              tarjeta de pose: es una card más de la grilla, no un botón aparte. */}
          {(onUploadCustomPose || onSubirPose) && (
            <AccionCard
              className="h-[var(--alto-fila,240px)] w-full"
              label="Subir pose"
              sublabel="Usá una foto tuya"
              icon={<Plus className="w-5 h-5" strokeWidth={2.5} />}
              // Con `onUploadCustomPose` el alta pasa a una vista de ESTE picker; sin
              // ella cae al sheet de siempre
              // (ADR-2026-08-24-subir-pose-sin-salir-del-panel). Esta rama lo miraba
              // sólo en la variante con "Automática", así que al sacar esa tarjeta el
              // panel se habría vuelto a ir al sheet para subir una pose.
              onClick={
                onUploadCustomPose ? () => setVista('subir') : onSubirPose
              }
            />
          )}
        </div>
        </>
      ) : (
      <HoverScrollCarousel
        key={`${poses.length}-${poses[0]?.id ?? ''}`}
        className="flex gap-3 overflow-x-auto -mx-5 px-5 scrollbar-hide animate-mode-swap"
        style={{
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          WebkitOverflowScrolling: 'touch',
        }}
        arrowInset={4}
      >
        {visibles.map((p) => {
          const isSel = selectedIds.includes(p.id);
          return (
            <button
              key={p.id}
              type="button"
              onClick={() => toggle(p.id)}
              className={`shrink-0 w-[140px] aspect-[9/16] rounded-2xl overflow-hidden bg-neutral-100 border-2 ${
                isSel ? 'border-brand-primary' : 'border-neutral-200'
              } transition-all relative active:scale-[0.97]`}
            >
              {p.imageUrl && (
                <img
                  src={p.imageUrl}
                  alt={p.nombre}
                  loading="lazy"
                  className="w-full h-full object-cover"
                />
              )}
              {/* El nombre DENTRO de la foto, abajo, sobre un degradé negro corto —
                  ADR-2026-09-04-las-poses-muestran-su-nombre-sobre-la-foto. Es la misma
                  anatomía que las tarjetas de PRODUCTO de este modo
                  (`nombreSobreLaFoto` en `PickProductPanelModelo`): el degradé arranca
                  transparente y no llega ni a un sexto de la tarjeta, así que separa el
                  texto del fondo sin tapar la pose. */}
              <span className="absolute inset-x-0 bottom-0 px-2.5 pt-8 pb-2 bg-gradient-to-t from-black/85 via-black/45 to-transparent">
                <span className="block text-left text-[12px] font-bold text-white truncate">
                  {p.nombre}
                </span>
              </span>
              {isSel && (
                <span className="absolute top-2 right-2 w-7 h-7 rounded-full inline-flex items-center justify-center shadow-md ring-2 ring-white bg-brand-primary text-white">
                  <Check className="w-4 h-4" strokeWidth={3} />
                </span>
              )}
            </button>
          );
        })}
        {/* Última card: el carrusel corta en 12 poses, así que acá la salida a
            la grilla completa no es sólo un atajo — es la única forma de ver
            el resto sin volver al link del header. */}
        <AccionCard
          className="shrink-0 w-[140px] aspect-[9/16]"
          sublabel={`${poses.length} poses`}
          onClick={onOpenSheet}
        />
      </HoverScrollCarousel>
      )}
    </div>
  );
};
